import React from 'react';
import { Alert, Button, Text } from '@mantine/core';
import { IconAlertCircle, IconRefresh } from '@tabler/icons-react';

// A failed dynamic import cannot be retried in place: React.lazy caches the rejection
// forever, so re-rendering throws the same error. Only a full reload recovers. This is
// the normal aftermath of a deploy, when a cached index.html points at chunk hashes that
// no longer exist.
const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError' ||
  /Loading chunk \S+ failed/i.test(error?.message || '') ||
  /Loading CSS chunk/i.test(error?.message || '');

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const staleBuild = isChunkLoadError(this.state.error);
      return (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <Alert
            icon={<IconAlertCircle size="1rem" />}
            title="Something went wrong"
            color="red"
            variant="light"
          >
            <Text size="sm" mb="md">
              {staleBuild
                ? 'A newer version of GainTime is available. Reload to continue.'
                : 'The application encountered an error while loading. This might be due to a network issue or a temporary problem.'}
            </Text>
            <Button
              leftIcon={<IconRefresh size="1rem" />}
              onClick={this.handleRetry}
              variant="light"
              size="sm"
            >
              {staleBuild ? 'Reload app' : 'Try Again'}
            </Button>
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
